// src/components/admin/VideosPorCategoriaAdmin.js
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, storage, auth } from '../../firebase/config';
import { signOut } from 'firebase/auth';
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  writeBatch
} from 'firebase/firestore';
import {
  ref as refStorage,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import {
  FaTrash,
  FaEdit,
  FaUpload,
  FaArrowLeft,
  FaTimesCircle,
  FaUsers,
  FaBolt,
  FaUserMinus,
  FaSearch,
  FaHome,
  FaSignOutAlt,
  FaVideo,
  FaChevronDown,
  FaChevronUp,
  FaCommentDots,
  FaSyncAlt,
  FaPlusCircle,
  FaCloudUploadAlt,
  FaCheckCircle,
  FaSortNumericDown
} from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Select from 'react-select';

function normalize(str = '') {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

function prettyCategoryName(folder = '') {
  const sinFecha = folder.replace(/^\d+(?:_\d+)*_/, '').replace(/_/g, ' ');
  if (sinFecha === 'calentamiento') return 'Anexos';
  return sinFecha.charAt(0).toUpperCase() + sinFecha.slice(1);
}

// Extrae el día del video ("Día 1", "Día 2", etc.)
function extraerDia(vid = {}) {
  const texto = `${vid.nombreVisible || ''} ${vid.nombreArchivo || ''} ${vid.descripcion || ''}`.toLowerCase();
  if (texto.includes('dia 4') || texto.includes('día 4')) return 'Día 4';
  if (texto.includes('dia 3') || texto.includes('día 3')) return 'Día 3';
  if (texto.includes('dia 2') || texto.includes('día 2')) return 'Día 2';
  if (texto.includes('dia 1') || texto.includes('día 1')) return 'Día 1';

  const asignado = vid.assignedTo?.[0]?.rutina;
  if (asignado) return asignado;

  return 'Día 1';
}

function diaAIndice(diaStr = '') {
  switch (diaStr) {
    case 'Día 1': return 1;
    case 'Día 2': return 2;
    case 'Día 3': return 3;
    case 'Día 4': return 4;
    default: return 5;
  }
}

function extraerNumeroInicial(texto = '') {
  const match = String(texto).match(/^\s*(\d+)\s*[-–.]/);
  return match ? parseInt(match[1], 10) : null;
}

function cambiarNumeroInicial(texto = '', nuevoNum, diaSugerido = 'Día 1') {
  let limpio = String(texto).replace(/^\s*\d+\s*[-–.]\s*/, '').trim();
  const regexDia = /^D[ií]a\s*\d+\s*[-–.]\s*/i;
  if (regexDia.test(limpio)) {
    return `${nuevoNum} - ${limpio}`;
  }
  return `${nuevoNum} - ${diaSugerido} - ${limpio}`;
}

async function commitEnLotes(operaciones) {
  const CHUNK_SIZE = 400;
  for (let i = 0; i < operaciones.length; i += CHUNK_SIZE) {
    const batch = writeBatch(db);
    const chunk = operaciones.slice(i, i + CHUNK_SIZE);
    chunk.forEach((op) => op(batch));
    await batch.commit();
  }
}

function ordenarVideosPorDiaYNumero(vids = []) {
  return [...vids].sort((a, b) => {
    const diaA = diaAIndice(extraerDia(a));
    const diaB = diaAIndice(extraerDia(b));

    if (diaA !== diaB) {
      return diaA - diaB;
    }

    const numA = extraerNumeroInicial(a.nombreVisible || a.nombreArchivo);
    const numB = extraerNumeroInicial(b.nombreVisible || b.nombreArchivo);

    if (numA !== null && numB !== null) return numA - numB;
    if (numA !== null) return -1;
    if (numB !== null) return 1;

    const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return ta - tb;
  });
}

const APP_PRIMARY = '#b03060';

export default function VideosPorCategoriaAdmin() {
  const { categoria } = useParams();
  const navigate = useNavigate();

  const [videos, setVideos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  /* Modal Eliminar Video */
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);

  /* Modal Asignar Plan Completo */
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [bulkUid, setBulkUid] = useState('');
  const [bulkPlan, setBulkPlan] = useState('');
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);

  /* Modal Quitar Plan Completo */
  const [showBulkRemoveModal, setShowBulkRemoveModal] = useState(false);
  const [bulkRemoveUid, setBulkRemoveUid] = useState('');
  const [bulkRemovePlan, setBulkRemovePlan] = useState('');
  const [isBulkRemoving, setIsBulkRemoving] = useState(false);

  /* Modal Editar Video */
  const [showEditModal, setShowEditModal] = useState(false);
  const [editNombre, setEditNombre] = useState('');
  const [editDescripcion, setEditDescripcion] = useState('');
  const [videoToEdit, setVideoToEdit] = useState(null);

  /* Modal Descripción */
  const [modalDescripcion, setModalDescripcion] = useState(null);

  /* Reemplazar video */
  const [videoToReplace, setVideoToReplace] = useState(null);
  const replaceFileInputRef = useRef();

  /* Modal Subida Normal */
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadDia, setUploadDia] = useState('Día 1');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadNombre, setUploadNombre] = useState('');
  const [uploadDescripcion, setUploadDescripcion] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const uploadFileInputRef = useRef();

  /* Modal Insertar Video en Medio */
  const [showInsertModal, setShowInsertModal] = useState(false);
  const [insertAfterVideo, setInsertAfterVideo] = useState(null);
  const [insertFile, setInsertFile] = useState(null);
  const [insertNombre, setInsertNombre] = useState('');
  const [insertDescripcion, setInsertDescripcion] = useState('');
  const [isInserting, setIsInserting] = useState(false);
  const insertFileInputRef = useRef();

  /* Selects de asignación individual */
  const [selectedUid, setSelectedUid] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [selectedRutina, setSelectedRutina] = useState('');

  /* Acordeón de asignaciones */
  const [openAssignments, setOpenAssignments] = useState({});

  const toggleOpenAssignments = (videoId) => {
    setOpenAssignments((prev) => ({
      ...prev,
      [videoId]: !prev[videoId]
    }));
  };

  /* Modal de Recomendación */
  const [recommendationText, setRecommendationText] = useState('');
  const [showRecModal, setShowRecModal] = useState(false);
  const [videoRecTarget, setVideoRecTarget] = useState(null);
  const [recForUid, setRecForUid] = useState('');

  /* Buscador y Paginación */
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 15;

  /* Reorganizar y compactar numeración */
  const ejecutarRecompactacionTotal = useCallback(async (videosBase) => {
    const vidsAOrganizar = videosBase || videos;
    if (vidsAOrganizar.length === 0) return;

    const agrupadosPorDia = { 'Día 1': [], 'Día 2': [], 'Día 3': [], 'Día 4': [] };

    vidsAOrganizar.forEach((v) => {
      const d = extraerDia(v);
      if (!agrupadosPorDia[d]) agrupadosPorDia[d] = [];
      agrupadosPorDia[d].push(v);
    });

    const operaciones = [];
    const videosActualizados = [];

    Object.keys(agrupadosPorDia).forEach((dia) => {
      const listaDia = agrupadosPorDia[dia];
      listaDia.forEach((vid, index) => {
        const nuevoNum = index + 1;
        const nombreActual = vid.nombreVisible || vid.nombreArchivo || '';
        const nuevoNombre = cambiarNumeroInicial(nombreActual, nuevoNum, dia);

        const asignacionesActualizadas = (vid.assignedTo || []).map((asig) => ({
          ...asig,
          orden: nuevoNum,
          indiceGlobal: nuevoNum,
          rutina: dia
        }));

        const vidRef = doc(db, 'videos', vid.id);
        operaciones.push((batch) =>
          batch.update(vidRef, {
            nombreVisible: nuevoNombre,
            assignedTo: asignacionesActualizadas
          })
        );

        videosActualizados.push({
          ...vid,
          nombreVisible: nuevoNombre,
          assignedTo: asignacionesActualizadas
        });
      });
    });

    if (operaciones.length > 0) {
      await commitEnLotes(operaciones);
    }

    setVideos(ordenarVideosPorDiaYNumero(videosActualizados));
  }, [videos]);

  const handleBotonReorganizarManual = async () => {
    const toastId = toast.loading('Reorganizando toda la lista para que empiece desde el 1...');
    try {
      await ejecutarRecompactacionTotal(videos);
      toast.update(toastId, {
        render: '¡Listo! Todos los días ahora arrancan ordenados desde el 1.',
        type: 'success',
        isLoading: false,
        autoClose: 3500
      });
    } catch (err) {
      console.error('Error al reorganizar:', err);
      toast.update(toastId, {
        render: 'Error al reorganizar: ' + err.message,
        type: 'error',
        isLoading: false,
        autoClose: 4000
      });
    }
  };

  /* Cargar datos desde Firestore */
  const cargarDatos = useCallback(async () => {
    try {
      const q = query(collection(db, 'videos'), where('categoria', '==', categoria));
      const snap = await getDocs(q);
      const vids = snap.docs.map((d) => {
        const dat = d.data();
        return {
          id: d.id,
          nombreArchivo: dat.nombreArchivo,
          storagePath: dat.storagePath,
          url: dat.url,
          tipo: dat.tipo || 'video',
          descripcion: dat.descripcion || '',
          assignedTo: dat.assignedTo || [],
          recomendaciones: dat.recomendaciones || {},
          nombreVisible: dat.nombreVisible || '',
          timestamp: dat.timestamp?.toDate ? dat.timestamp.toDate() : dat.timestamp || null
        };
      });

      const vidsOrdenados = ordenarVideosPorDiaYNumero(vids);
      setVideos(vidsOrdenados);

      const primeroDia1 = vidsOrdenados.find((v) => extraerDia(v) === 'Día 1');
      if (primeroDia1) {
        const numPrimero = extraerNumeroInicial(primeroDia1.nombreVisible || primeroDia1.nombreArchivo);
        if (numPrimero !== null && numPrimero > 1) {
          ejecutarRecompactacionTotal(vidsOrdenados);
        }
      }

      const su = await getDocs(collection(db, 'usuarios'));
      setUsuarios(
        su.docs.map((u) => {
          const d = u.data();
          const nomCompleto = `${d.nombre || ''} ${d.apellido || ''}`.trim();
          return {
            uid: u.id,
            nombre: nomCompleto || d.email || u.id,
            email: d.email || '',
            planEntrenamiento: d.planEntrenamiento || d.plan || ''
          };
        })
      );
    } catch (err) {
      console.error('Error al cargar datos:', err);
      toast.error('Error al cargar datos.');
    }
  }, [categoria, ejecutarRecompactacionTotal]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  /* Opciones de Plan originales */
  const planOptions = useMemo(
    () => [
      { label: 'Plan Mujer Basico Gym', value: 'Plan Mujer Basico Gym' },
      { label: 'Plan Mujer Basico Casa', value: 'Plan Mujer Basico Casa' },
      { label: 'Plan Mujer Intermedio Gym', value: 'Plan Mujer Intermedio Gym' },
      { label: 'Plan Mujer Intermedio Casa', value: 'Plan Mujer Intermedio Casa' },
      { label: 'Plan Mujer Avanzado Gym', value: 'Plan Mujer Avanzado Gym' },
      { label: 'Plan Mujer Avanzado Casa', value: 'Plan Mujer Avanzado Casa' },

      { label: 'Plan Hombre Basico Gym', value: 'Plan Hombre Basico Gym' },
      { label: 'Plan Hombre Basico Casa', value: 'Plan Hombre Basico Casa' },
      { label: 'Plan Hombre Intermedio Gym', value: 'Plan Hombre Intermedio Gym' },
      { label: 'Plan Hombre Intermedio Casa', value: 'Plan Hombre Intermedio Casa' },
      { label: 'Plan Hombre Avanzado Gym', value: 'Plan Hombre Avanzado Gym' },
      { label: 'Plan Hombre Avanzado Casa', value: 'Plan Hombre Avanzado Casa' }
    ],
    []
  );

  /* Auto-sugerir plan */
  useEffect(() => {
    if (showBulkAssignModal || showBulkRemoveModal) {
      const match = planOptions.find((p) =>
        normalize(p.value).includes(normalize(categoria.replace(/_/g, ' ')))
      );
      if (match) {
        if (showBulkAssignModal) setBulkPlan(match.value);
        if (showBulkRemoveModal) setBulkRemovePlan(match.value);
      } else if (planOptions.length > 0) {
        if (showBulkAssignModal) setBulkPlan(planOptions[0].value);
        if (showBulkRemoveModal) setBulkRemovePlan(planOptions[0].value);
      }
    }
  }, [showBulkAssignModal, showBulkRemoveModal, categoria, planOptions]);

  /* Asignación masiva */
  const handleBulkAssign = async () => {
    if (!bulkUid) return toast.error('Por favor selecciona un usuario.');
    if (!bulkPlan) return toast.error('Por favor selecciona el nombre del plan.');
    if (videos.length === 0) return toast.error('No hay videos en esta categoría para asignar.');

    const usuarioObj = usuarios.find((u) => u.uid === bulkUid);
    const nombreUsuario = usuarioObj ? usuarioObj.nombre : bulkUid;

    setIsBulkAssigning(true);

    try {
      const operaciones = [];
      const updatedVideos = [];

      const contadoresPorDia = { 'Día 1': 0, 'Día 2': 0, 'Día 3': 0, 'Día 4': 0 };

      for (let i = 0; i < videos.length; i++) {
        const vid = videos[i];
        const rutinaAsignada = extraerDia(vid);
        contadoresPorDia[rutinaAsignada] = (contadoresPorDia[rutinaAsignada] || 0) + 1;
        const posicionEnDia = contadoresPorDia[rutinaAsignada];

        let nuevoAssignedTo = (vid.assignedTo || []).filter(
          (a) => !(a.uid === bulkUid && a.plan === bulkPlan && a.rutina === rutinaAsignada)
        );

        nuevoAssignedTo.push({
          uid: bulkUid,
          nombreUsuario,
          plan: bulkPlan,
          rutina: rutinaAsignada,
          orden: posicionEnDia,
          indiceGlobal: i + 1
        });

        const vidRef = doc(db, 'videos', vid.id);
        operaciones.push((b) => b.update(vidRef, { assignedTo: nuevoAssignedTo }));
        updatedVideos.push({ ...vid, assignedTo: nuevoAssignedTo });
      }

      const userRef = doc(db, 'usuarios', bulkUid);
      operaciones.push((b) =>
        b.set(
          userRef,
          {
            planEntrenamiento: bulkPlan,
            plan: bulkPlan,
            planSeleccionado: bulkPlan
          },
          { merge: true }
        )
      );

      await commitEnLotes(operaciones);

      setVideos(updatedVideos);
      toast.success(`¡Plan "${bulkPlan}" asignado por completo a ${nombreUsuario}!`);
      setShowBulkAssignModal(false);
      setBulkUid('');
    } catch (err) {
      console.error('Error al asignar plan completo:', err);
      toast.error('Ocurrió un error al procesar la asignación masiva.');
    } finally {
      setIsBulkAssigning(false);
    }
  };

  /* Quitar plan completo */
  const handleBulkRemove = async () => {
    if (!bulkRemoveUid) return toast.error('Por favor selecciona un usuario.');
    if (!bulkRemovePlan) return toast.error('Por favor selecciona el plan a desasignar.');

    const usuarioObj = usuarios.find((u) => u.uid === bulkRemoveUid);
    const nombreUsuario = usuarioObj ? usuarioObj.nombre : bulkRemoveUid;

    setIsBulkRemoving(true);

    try {
      const operaciones = [];
      const updatedVideos = [];
      let conteoRemovidos = 0;

      for (const vid of videos) {
        const tieneAsignacion = (vid.assignedTo || []).some(
          (a) => a.uid === bulkRemoveUid && a.plan === bulkRemovePlan
        );

        if (tieneAsignacion) {
          conteoRemovidos++;
          const nuevoAssignedTo = (vid.assignedTo || []).filter(
            (a) => !(a.uid === bulkRemoveUid && a.plan === bulkRemovePlan)
          );
          const vidRef = doc(db, 'videos', vid.id);
          operaciones.push((b) => b.update(vidRef, { assignedTo: nuevoAssignedTo }));
          updatedVideos.push({ ...vid, assignedTo: nuevoAssignedTo });
        } else {
          updatedVideos.push(vid);
        }
      }

      if (usuarioObj?.planEntrenamiento === bulkRemovePlan || usuarioObj?.plan === bulkRemovePlan) {
        const userRef = doc(db, 'usuarios', bulkRemoveUid);
        operaciones.push((b) =>
          b.update(userRef, {
            planEntrenamiento: '',
            plan: null
          })
        );
      }

      await commitEnLotes(operaciones);

      setVideos(updatedVideos);
      toast.success(
        `Se eliminaron las asignaciones de "${bulkRemovePlan}" para ${nombreUsuario} (${conteoRemovidos} video(s)).`
      );
      setShowBulkRemoveModal(false);
      setBulkRemoveUid('');
    } catch (err) {
      console.error('Error al quitar plan completo:', err);
      toast.error('Ocurrió un error al eliminar el plan al usuario.');
    } finally {
      setIsBulkRemoving(false);
    }
  };

  /* Filtrado y Paginación */
  const filteredVideos = videos.filter((v) =>
    normalize(v.nombreVisible || v.nombreArchivo).includes(normalize(search))
  );
  const totalPages = Math.max(1, Math.ceil(filteredVideos.length / perPage));
  const paginatedVideos = filteredVideos.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  /* Modal de subida de video */
  const handleOpenUploadModal = () => {
    setUploadDia('Día 1');
    setUploadFile(null);
    setUploadNombre('1 - Día 1 - ');
    setUploadDescripcion('');
    setShowUploadModal(true);
  };

  const handleExecuteUpload = async () => {
    if (!uploadFile) return toast.error('Por favor selecciona un archivo de video.');
    if (!uploadNombre.trim()) return toast.error('Ingresa el nombre visible del ejercicio.');

    setIsUploading(true);
    const toastId = toast.loading(`Subiendo video y posicionándolo al inicio de ${uploadDia}...`);

    try {
      const timestamp = Date.now();
      const nombreLimpio = uploadFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `mft/${categoria}/${timestamp}_${nombreLimpio}`;
      const storageReference = refStorage(storage, storagePath);

      await uploadBytes(storageReference, uploadFile);
      const url = await getDownloadURL(storageReference);
      const tipoArchivo = uploadFile.type.startsWith('image') ? 'imagen' : 'video';

      const operaciones = [];

      videos.forEach((vid) => {
        const diaDeEsteVideo = extraerDia(vid);
        if (diaDeEsteVideo === uploadDia) {
          const numActual = extraerNumeroInicial(vid.nombreVisible || vid.nombreArchivo) || 1;
          const nuevoNombre = cambiarNumeroInicial(vid.nombreVisible, numActual + 1, uploadDia);

          const asignacionesActualizadas = (vid.assignedTo || []).map((asig) => ({
            ...asig,
            orden: asig.orden !== null ? asig.orden + 1 : asig.orden,
            indiceGlobal: asig.indiceGlobal !== null ? asig.indiceGlobal + 1 : asig.indiceGlobal
          }));

          const docRef = doc(db, 'videos', vid.id);
          operaciones.push((batch) =>
            batch.update(docRef, {
              nombreVisible: nuevoNombre,
              assignedTo: asignacionesActualizadas
            })
          );
        }
      });

      const nuevoDocRef = doc(collection(db, 'videos'));
      operaciones.push((batch) =>
        batch.set(nuevoDocRef, {
          categoria,
          nombreArchivo: uploadFile.name,
          storagePath,
          nombreVisible: uploadNombre.trim(),
          descripcion: uploadDescripcion.trim(),
          url,
          tipo: tipoArchivo,
          vistas: 0,
          assignedTo: [],
          recomendaciones: {},
          timestamp: new Date()
        })
      );

      await commitEnLotes(operaciones);
      await cargarDatos();

      toast.update(toastId, {
        render: `¡Video subido exitosamente como el N° 1 de ${uploadDia}!`,
        type: 'success',
        isLoading: false,
        autoClose: 3500
      });

      setShowUploadModal(false);
      setUploadFile(null);
      setUploadNombre('');
      setUploadDescripcion('');
    } catch (err) {
      console.error('Error al subir video:', err);
      toast.update(toastId, {
        render: 'Error al subir el video: ' + err.message,
        type: 'error',
        isLoading: false,
        autoClose: 4000
      });
    } finally {
      setIsUploading(false);
    }
  };

  /* Reemplazar video */
  const handleTriggerReplace = (vid) => {
    setVideoToReplace(vid);
    replaceFileInputRef.current.click();
  };

  const handleExecuteReplace = async (e) => {
    const file = e.target.files[0];
    if (!file || !videoToReplace) return;

    const toastId = toast.loading('Reemplazando video en Firebase...');

    try {
      if (videoToReplace.storagePath) {
        try {
          await deleteObject(refStorage(storage, videoToReplace.storagePath));
        } catch (errOld) {
          console.warn('El archivo anterior no existía en Storage:', errOld);
        }
      }

      const timestamp = Date.now();
      const nombreLimpio = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `mft/${categoria}/${timestamp}_${nombreLimpio}`;
      const storageReference = refStorage(storage, storagePath);

      await uploadBytes(storageReference, file);
      const url = await getDownloadURL(storageReference);
      const tipoArchivo = file.type.startsWith('image') ? 'imagen' : 'video';

      const docRef = doc(db, 'videos', videoToReplace.id);
      await updateDoc(docRef, {
        url,
        nombreArchivo: file.name,
        storagePath,
        tipo: tipoArchivo
      });

      setVideos((prev) =>
        prev.map((v) =>
          v.id === videoToReplace.id
            ? { ...v, url, nombreArchivo: file.name, storagePath, tipo: tipoArchivo }
            : v
        )
      );

      toast.update(toastId, {
        render: '¡Video reemplazado con éxito sin perder su posición ni asignaciones!',
        type: 'success',
        isLoading: false,
        autoClose: 3500
      });
    } catch (err) {
      console.error('Error al reemplazar video:', err);
      toast.update(toastId, {
        render: 'No se pudo reemplazar: ' + err.message,
        type: 'error',
        isLoading: false,
        autoClose: 4000
      });
    } finally {
      setVideoToReplace(null);
      if (replaceFileInputRef.current) replaceFileInputRef.current.value = '';
    }
  };

  /* Insertar video en medio */
  const handleOpenInsertModal = (vid) => {
    setInsertAfterVideo(vid);
    const diaDelVideo = extraerDia(vid);
    const numActual = extraerNumeroInicial(vid.nombreVisible || vid.nombreArchivo);
    const nuevoNum = numActual !== null ? numActual + 1 : 1;

    setInsertNombre(`${nuevoNum} - ${diaDelVideo} - `);
    setInsertDescripcion('');
    setInsertFile(null);
    setShowInsertModal(true);
  };

  const handleExecuteInsert = async () => {
    if (!insertFile) return toast.error('Por favor selecciona un archivo de video.');
    if (!insertNombre.trim()) return toast.error('Ingresa el nombre visible del ejercicio.');

    setIsInserting(true);
    const toastId = toast.loading('Insertando y reordenando la rutina...');

    try {
      const diaObjetivo = extraerDia(insertAfterVideo);
      const numBase = extraerNumeroInicial(insertAfterVideo.nombreVisible || insertAfterVideo.nombreArchivo) || 0;

      const videosPosterioresMismoDia = videos.filter((v) => {
        const diaV = extraerDia(v);
        const numV = extraerNumeroInicial(v.nombreVisible || v.nombreArchivo) || 0;
        return diaV === diaObjetivo && numV > numBase;
      });

      const timestamp = Date.now();
      const nombreLimpio = insertFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `mft/${categoria}/${timestamp}_${nombreLimpio}`;
      const storageReference = refStorage(storage, storagePath);

      await uploadBytes(storageReference, insertFile);
      const url = await getDownloadURL(storageReference);
      const tipoArchivo = insertFile.type.startsWith('image') ? 'imagen' : 'video';

      const operaciones = [];

      videosPosterioresMismoDia.forEach((vid) => {
        const numActual = extraerNumeroInicial(vid.nombreVisible || vid.nombreArchivo);
        if (numActual !== null) {
          const nuevoNombre = cambiarNumeroInicial(vid.nombreVisible, numActual + 1, diaObjetivo);

          const asignacionesActualizadas = (vid.assignedTo || []).map((asig) => ({
            ...asig,
            orden: asig.orden !== null ? asig.orden + 1 : asig.orden,
            indiceGlobal: asig.indiceGlobal !== null ? asig.indiceGlobal + 1 : asig.indiceGlobal
          }));

          const docRef = doc(db, 'videos', vid.id);
          operaciones.push((batch) =>
            batch.update(docRef, {
              nombreVisible: nuevoNombre,
              assignedTo: asignacionesActualizadas
            })
          );
        }
      });

      const nuevoDocRef = doc(collection(db, 'videos'));
      operaciones.push((batch) =>
        batch.set(nuevoDocRef, {
          categoria,
          nombreArchivo: insertFile.name,
          storagePath,
          nombreVisible: insertNombre.trim(),
          descripcion: insertDescripcion.trim(),
          url,
          tipo: tipoArchivo,
          vistas: 0,
          assignedTo: [],
          recomendaciones: {},
          timestamp: new Date()
        })
      );

      await commitEnLotes(operaciones);
      await cargarDatos();

      toast.update(toastId, {
        render: `¡Video insertado en ${diaObjetivo}! Se renumeraron los ejercicios posteriores de ese día.`,
        type: 'success',
        isLoading: false,
        autoClose: 3500
      });

      setShowInsertModal(false);
      setInsertAfterVideo(null);
      setInsertFile(null);
      setInsertNombre('');
      setInsertDescripcion('');
    } catch (err) {
      console.error('Error al insertar video:', err);
      toast.update(toastId, {
        render: 'Error al insertar video: ' + err.message,
        type: 'error',
        isLoading: false,
        autoClose: 4000
      });
    } finally {
      setIsInserting(false);
    }
  };

  /* Eliminar video */
  const openDeleteModal = (vid) => {
    setVideoToDelete(vid);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!videoToDelete) return;
    const toastId = toast.loading('Eliminando y reordenando la lista...');

    try {
      const ruta = videoToDelete.storagePath || `mft/${categoria}/${videoToDelete.nombreArchivo}`;
      try {
        await deleteObject(refStorage(storage, ruta));
      } catch (errStorage) {
        console.warn('El archivo ya no existía en Storage:', errStorage);
      }

      await deleteDoc(doc(db, 'videos', videoToDelete.id));

      const diaDelEliminado = extraerDia(videoToDelete);
      const restantes = videos.filter((x) => x.id !== videoToDelete.id);

      const operaciones = [];
      const videosActualizados = [];

      let contadorDia = 0;

      restantes.forEach((vid) => {
        const diaDeEste = extraerDia(vid);

        if (diaDeEste === diaDelEliminado) {
          contadorDia++;
          const nuevoNumero = contadorDia;
          const nuevoNombre = cambiarNumeroInicial(vid.nombreVisible, nuevoNumero, diaDelEliminado);

          const asignacionesActualizadas = (vid.assignedTo || []).map((asig) => ({
            ...asig,
            orden: nuevoNumero,
            indiceGlobal: nuevoNumero
          }));

          const docRef = doc(db, 'videos', vid.id);
          operaciones.push((batch) =>
            batch.update(docRef, {
              nombreVisible: nuevoNombre,
              assignedTo: asignacionesActualizadas
            })
          );

          videosActualizados.push({
            ...vid,
            nombreVisible: nuevoNombre,
            assignedTo: asignacionesActualizadas
          });
        } else {
          videosActualizados.push(vid);
        }
      });

      if (operaciones.length > 0) {
        await commitEnLotes(operaciones);
      }

      setVideos(ordenarVideosPorDiaYNumero(videosActualizados));

      toast.update(toastId, {
        render: `¡Video eliminado y numeración de ${diaDelEliminado} reordenada desde el 1!`,
        type: 'success',
        isLoading: false,
        autoClose: 3000
      });
    } catch (err) {
      console.error('Error al eliminar archivo:', err);
      toast.update(toastId, {
        render: 'No se pudo eliminar el archivo: ' + err.message,
        type: 'error',
        isLoading: false,
        autoClose: 4000
      });
    } finally {
      setShowDeleteModal(false);
      setVideoToDelete(null);
    }
  };

  /* Editar video */
  const openEditModal = (vid) => {
    setVideoToEdit(vid);
    setEditNombre(vid.nombreVisible || '');
    setEditDescripcion(vid.descripcion || '');
    setShowEditModal(true);
  };

  const saveEdits = async () => {
    if (!videoToEdit) return;
    if (!editNombre.trim()) return toast.error('El nombre no puede estar vacío.');

    try {
      const docRef = doc(db, 'videos', videoToEdit.id);
      await updateDoc(docRef, {
        nombreVisible: editNombre.trim(),
        descripcion: editDescripcion.trim()
      });

      const actualizados = videos.map((x) =>
        x.id === videoToEdit.id
          ? { ...x, nombreVisible: editNombre.trim(), descripcion: editDescripcion.trim() }
          : x
      );

      setVideos(ordenarVideosPorDiaYNumero(actualizados));
      toast.success('Cambios guardados.');
      setShowEditModal(false);
      setVideoToEdit(null);
    } catch (err) {
      console.error('Error al guardar cambios:', err);
      toast.error('No se pudieron guardar los cambios.');
    }
  };

  /* Asignar Usuario Individual */
  const handleAssignUser = async (videoId, uid, plan, rutina) => {
    if (!uid || !plan || !rutina) {
      return toast.error('Debe seleccionar usuario, plan y rutina (Día 1 a Día 4).');
    }
    const video = videos.find((v) => v.id === videoId);
    if (!video) return;

    const usuarioObj = usuarios.find((u) => u.uid === uid);
    const nombreUsuario = usuarioObj ? usuarioObj.nombre : uid;

    const videosDeLaRutina = videos.filter((v) =>
      (v.assignedTo || []).some((a) => a.uid === uid && a.rutina === rutina)
    );
    const proximoOrden = videosDeLaRutina.length + 1;

    const updatedAssigned = [
      ...(video.assignedTo || []).filter(
        (a) => !(a.uid === uid && a.plan === plan && a.rutina === rutina)
      ),
      {
        uid,
        nombreUsuario,
        plan,
        rutina,
        orden: proximoOrden,
        indiceGlobal: proximoOrden
      }
    ];

    try {
      const docRef = doc(db, 'videos', videoId);
      await updateDoc(docRef, { assignedTo: updatedAssigned });

      try {
        await setDoc(
          doc(db, 'usuarios', uid),
          { planEntrenamiento: plan, plan: plan },
          { merge: true }
        );
      } catch (errUser) {
        console.warn('No se pudo actualizar plan en usuario:', errUser);
      }

      setVideos((v) =>
        v.map((x) => (x.id === videoId ? { ...x, assignedTo: updatedAssigned } : x))
      );

      setSelectedUid('');
      setSelectedPlan('');
      setSelectedRutina('');
      toast.success('Ejercicio asignado correctamente.');
    } catch (err) {
      console.error('Error al asignar usuario:', err);
      toast.error('No se pudo asignar el usuario.');
    }
  };

  /* Quitar Asignación Individual */
  const handleRemoveUser = async (videoId, uid, plan, rutina) => {
    try {
      const video = videos.find((v) => v.id === videoId);
      if (!video) return;
      const updatedAssigned = (video.assignedTo || []).filter(
        (a) => !(a.uid === uid && a.plan === plan && a.rutina === rutina)
      );
      const docRef = doc(db, 'videos', videoId);
      await updateDoc(docRef, { assignedTo: updatedAssigned });
      setVideos((v) =>
        v.map((x) => (x.id === videoId ? { ...x, assignedTo: updatedAssigned } : x))
      );
      toast.success('Asignación removida.');
    } catch (err) {
      console.error('Error al remover asignación:', err);
      toast.error('No se pudo remover la asignación.');
    }
  };

  /* Recomendaciones */
  const openRecommendationFor = (video, uid) => {
    setVideoRecTarget(video);
    setRecForUid(uid);
    const existing = (video.recomendaciones || {})[uid] || '';
    setRecommendationText(existing);
    setShowRecModal(true);
  };

  const saveRecommendation = async () => {
    if (!videoRecTarget || !recForUid) return toast.error('Falta video o usuario destino.');
    try {
      const docRef = doc(db, 'videos', videoRecTarget.id);
      const nuevasRec = { ...(videoRecTarget.recomendaciones || {}) };
      nuevasRec[recForUid] = recommendationText.trim();
      await updateDoc(docRef, { recomendaciones: nuevasRec });
      setVideos((v) =>
        v.map((x) => (x.id === videoRecTarget.id ? { ...x, recomendaciones: nuevasRec } : x))
      );
      toast.success('Recomendación guardada.');
      setShowRecModal(false);
      setRecommendationText('');
      setVideoRecTarget(null);
      setRecForUid('');
    } catch (err) {
      console.error('Error al guardar recomendación:', err);
      toast.error('No se pudo guardar la recomendación.');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (err) {
      alert('Error al cerrar sesión: ' + err.message);
    }
  };

  const usuarioOptions = usuarios.map((u) => ({
    label: u.nombre,
    value: u.uid,
    raw: u
  }));

  const rutinaOptions = Array.from({ length: 4 }, (_, i) => ({
    label: `Día ${i + 1}`,
    value: `Día ${i + 1}`
  }));

  const filterOption = (option, rawInput) => {
    const input = normalize(rawInput);
    const label = normalize(option.label);
    return label.includes(input);
  };

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: 10,
      borderColor: state.isFocused ? APP_PRIMARY : '#e2e8f0',
      boxShadow: state.isFocused ? '0 0 0 1px #b03060' : 'none',
      fontSize: 13,
      minHeight: 38,
      backgroundColor: '#ffffff',
      '&:hover': {
        borderColor: state.isFocused ? APP_PRIMARY : '#cbd5e1'
      }
    }),
    menu: (base) => ({
      ...base,
      borderRadius: 12,
      zIndex: 100,
      boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
    }),
    option: (base, state) => ({
      ...base,
      fontSize: 13,
      backgroundColor: state.isSelected ? APP_PRIMARY : state.isFocused ? '#fdf2f8' : '#ffffff',
      color: state.isSelected ? '#ffffff' : state.isFocused ? APP_PRIMARY : '#1e293b',
      cursor: 'pointer'
    })
  };

  return (
    <div style={styles.page}>
      <ToastContainer position="bottom-center" />

      {/* Input oculto para reemplazar archivo */}
      <input
        type="file"
        accept="video/*,image/jpeg,image/png,image/jpg"
        ref={replaceFileInputRef}
        style={{ display: 'none' }}
        onChange={handleExecuteReplace}
      />

      <div style={styles.container}>
        {/* Cabecera */}
        <div style={styles.headerRow}>
          <button onClick={() => navigate('/admin/rutinas')} style={styles.backButton}>
            <FaArrowLeft size={13} /> Volver a categorías
          </button>
          <span style={styles.headerBadge}>Detalle de Categoría</span>
        </div>

        {/* Título */}
        <div style={styles.titleSection}>
          <h1 style={styles.title}>{prettyCategoryName(categoria)}</h1>
          <p style={styles.subtitle}>
            {videos.length} {videos.length === 1 ? 'archivo disponible' : 'archivos disponibles'} en esta sección
          </p>
        </div>

        {/* Barra de Acciones Globales */}
        <div style={styles.actionToolbar}>
          <div style={styles.leftActions}>
            <button onClick={handleOpenUploadModal} style={styles.btnUpload}>
              <FaUpload size={13} /> Subir archivo
            </button>
            <button
              onClick={handleBotonReorganizarManual}
              style={styles.btnReorganize}
              title="Recompacta y reescribe la numeración desde el 1 para todos los días"
            >
              <FaSortNumericDown size={13} /> Reorganizar desde 1
            </button>
          </div>

          <div style={styles.rightActions}>
            <button onClick={() => setShowBulkAssignModal(true)} style={styles.btnBulkAssign}>
              <FaBolt size={13} /> Asignar plan completo
            </button>
            <button onClick={() => setShowBulkRemoveModal(true)} style={styles.btnBulkRemove}>
              <FaUserMinus size={13} /> Quitar plan completo
            </button>
          </div>
        </div>

        {/* Buscador */}
        <div style={styles.searchWrapper}>
          <FaSearch style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Buscar video por nombre..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            style={styles.searchInput}
          />
        </div>

        {/* Listado de Videos en Formato Cards */}
        {paginatedVideos.length === 0 ? (
          <div style={styles.emptyCard}>
            <FaVideo size={36} color="#cbd5e1" style={{ marginBottom: 12 }} />
            <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>
              No se encontraron videos en esta categoría.
            </p>
          </div>
        ) : (
          <div style={styles.videosList}>
            {paginatedVideos.map((vid) => (
              <div key={vid.id} style={styles.videoCard}>
                {/* 1. Columna del Reproductor */}
                <div style={styles.mediaContainer}>
                  {vid.tipo === 'imagen' ? (
                    <img
                      src={vid.url}
                      alt={vid.nombreVisible}
                      style={styles.mediaElement}
                    />
                  ) : (
                    <video
                      src={vid.url}
                      controls
                      playsInline
                      preload="metadata"
                      style={styles.mediaElement}
                    />
                  )}
                  <span style={styles.mediaBadge}>
                    {vid.tipo === 'imagen' ? 'Foto' : 'Video'}
                  </span>
                </div>

                {/* 2. Columna de Información y Acciones sobre el Archivo */}
                <div style={styles.infoCol}>
                  <div style={styles.videoTitleRow}>
                    <h3 style={styles.videoTitle}>
                      {vid.nombreVisible || <em>(sin nombre asignado)</em>}
                    </h3>
                    <div style={styles.videoTopActions}>
                      <button
                        onClick={() => handleTriggerReplace(vid)}
                        style={styles.iconReplaceBtn}
                        title="Reemplazar archivo de este video (conserva nombre, orden y asignaciones)"
                      >
                        <FaSyncAlt size={12} />
                      </button>

                      <button
                        onClick={() => openEditModal(vid)}
                        style={styles.iconEditBtn}
                        title="Editar nombre y descripción"
                      >
                        <FaEdit size={13} />
                      </button>

                      <button
                        onClick={() => openDeleteModal(vid)}
                        style={styles.iconDeleteBtn}
                        title="Eliminar archivo"
                      >
                        <FaTrash size={13} />
                      </button>
                    </div>
                  </div>

                  <p
                    style={styles.videoDescription}
                    onClick={() => {
                      if (!vid.descripcion) return;
                      setModalDescripcion({
                        titulo: vid.nombreVisible || 'Descripción del ejercicio',
                        texto: vid.descripcion
                      });
                    }}
                    title="Haz clic para ver la descripción completa"
                  >
                    {vid.descripcion || <em>Sin descripción</em>}
                  </p>

                  <small style={styles.descHint}>
                    Toca la descripción para verla completa
                  </small>

                  <div style={{ marginTop: 12 }}>
                    <button
                      type="button"
                      onClick={() => handleOpenInsertModal(vid)}
                      style={styles.btnInsertAfter}
                    >
                      <FaPlusCircle size={13} color={APP_PRIMARY} />
                      <span>Insertar ejercicio después de este</span>
                    </button>
                  </div>
                </div>

                {/* 3. Columna de Asignaciones */}
                <div style={styles.assignCol}>
                  <div style={styles.assignTitleRow}>
                    <span style={styles.assignSectionLabel}>Asignar ejercicio a:</span>
                  </div>

                  <div style={styles.assignControlsGrid}>
                    <div style={{ flex: '1 1 140px' }}>
                      <Select
                        options={usuarioOptions}
                        placeholder="Usuario..."
                        isSearchable
                        filterOption={filterOption}
                        onChange={(o) => setSelectedUid(o ? o.value : '')}
                        value={usuarioOptions.find((u) => u.value === selectedUid) || null}
                        styles={customSelectStyles}
                      />
                    </div>

                    <div style={{ flex: '1 1 170px' }}>
                      <Select
                        options={planOptions}
                        placeholder="Plan..."
                        onChange={(o) => setSelectedPlan(o ? o.value : '')}
                        value={planOptions.find((p) => p.value === selectedPlan) || null}
                        styles={customSelectStyles}
                      />
                    </div>

                    <div style={{ width: 110, flexShrink: 0 }}>
                      <Select
                        options={rutinaOptions}
                        placeholder="Día..."
                        onChange={(o) => setSelectedRutina(o ? o.value : '')}
                        value={rutinaOptions.find((r) => r.value === selectedRutina) || null}
                        styles={customSelectStyles}
                      />
                    </div>

                    <button
                      onClick={() => handleAssignUser(vid.id, selectedUid, selectedPlan, selectedRutina)}
                      style={styles.btnAssignSingle}
                    >
                      Asignar
                    </button>
                  </div>

                  <div style={styles.assignedAccordionBox}>
                    {vid.assignedTo && vid.assignedTo.length > 0 ? (
                      <div>
                        <button
                          type="button"
                          onClick={() => toggleOpenAssignments(vid.id)}
                          style={{
                            ...styles.accordionTrigger,
                            backgroundColor: openAssignments[vid.id] ? '#fdf2f8' : '#f8fafc',
                            color: openAssignments[vid.id] ? APP_PRIMARY : '#475569'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <FaUsers size={12} color={APP_PRIMARY} />
                            <span>
                              {vid.assignedTo.length} alumno{vid.assignedTo.length > 1 ? 's' : ''} asignado{vid.assignedTo.length > 1 ? 's' : ''}
                            </span>
                          </div>
                          {openAssignments[vid.id] ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                        </button>

                        {openAssignments[vid.id] && (
                          <div style={styles.accordionContent}>
                            {vid.assignedTo.map((a) => (
                              <div
                                key={`${a.uid}-${a.plan}-${a.rutina}`}
                                style={styles.assignedUserItem}
                              >
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  <span style={styles.assignedUserName}>
                                    {a.nombreUsuario || a.uid}
                                  </span>
                                  <span style={styles.assignedUserPlan}>
                                    {a.plan} • <b style={{ color: APP_PRIMARY }}>{a.rutina}</b>
                                  </span>
                                </div>

                                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                  <button
                                    onClick={() => openRecommendationFor(vid, a.uid)}
                                    style={styles.btnRec}
                                    title="Escribir recomendación"
                                  >
                                    <FaCommentDots size={11} /> Indicación
                                  </button>

                                  <button
                                    onClick={() => handleRemoveUser(vid.id, a.uid, a.plan, a.rutina)}
                                    style={styles.btnRemoveAssignment}
                                    title="Quitar asignación"
                                  >
                                    <FaTimesCircle size={14} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={styles.unassignedLabel}>
                        — Sin alumnos asignados a este video —
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Paginación */}
        {totalPages > 1 && (
          <div style={styles.paginationRow}>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                ...styles.pageBtn,
                opacity: currentPage === 1 ? 0.4 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              ◀
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                style={{
                  ...styles.pageBtn,
                  backgroundColor: p === currentPage ? APP_PRIMARY : '#ffffff',
                  color: p === currentPage ? '#ffffff' : '#1e293b',
                  border: p === currentPage ? `1.5px solid ${APP_PRIMARY}` : '1.5px solid #e2e8f0',
                  fontWeight: p === currentPage ? '700' : '500'
                }}
              >
                {p}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                ...styles.pageBtn,
                opacity: currentPage === totalPages ? 0.4 : 1,
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              ▶
            </button>
          </div>
        )}
      </div>

      {/* MODAL: Subir Archivo Nuevo */}
      {showUploadModal && (
        <div style={modalOverlay} onClick={() => !isUploading && setShowUploadModal(false)}>
          <div style={modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, color: '#1e293b', fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
                <FaUpload color={APP_PRIMARY} /> Subir nuevo ejercicio
              </h3>
              {!isUploading && (
                <button
                  onClick={() => setShowUploadModal(false)}
                  style={styles.modalCloseIcon}
                >
                  ✕
                </button>
              )}
            </div>

            <p style={styles.modalDesc}>
              El ejercicio se posicionará como el <strong>número 1</strong> del día que elijas. Los demás ejercicios de ese día se desplazarán hacia abajo automáticamente.
            </p>

            <div style={{ marginBottom: 16 }}>
              <label style={styles.modalInputLabel}>1. Asignar al día:</label>
              <div style={styles.daySelectorGroup}>
                {['Día 1', 'Día 2', 'Día 3', 'Día 4'].map((d) => (
                  <button
                    key={d}
                    type="button"
                    disabled={isUploading}
                    onClick={() => {
                      setUploadDia(d);
                      const baseNombre = uploadFile ? uploadFile.name.replace(/\.[^/.]+$/, '').replace(/_/g, ' ') : '';
                      setUploadNombre(`1 - ${d} - ${baseNombre}`.trim());
                    }}
                    style={{
                      ...styles.daySelectBtn,
                      backgroundColor: uploadDia === d ? APP_PRIMARY : '#ffffff',
                      color: uploadDia === d ? '#ffffff' : '#334155',
                      borderColor: uploadDia === d ? APP_PRIMARY : '#cbd5e1'
                    }}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>

            <input
              type="file"
              accept="video/*,image/jpeg,image/png,image/jpg"
              ref={uploadFileInputRef}
              disabled={isUploading}
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files[0] || null;
                setUploadFile(file);
                if (file) {
                  const base = file.name.replace(/\.[^/.]+$/, '').replace(/_/g, ' ');
                  setUploadNombre(`1 - ${uploadDia} - ${base}`);
                }
              }}
            />

            <div style={{ marginBottom: 16 }}>
              <label style={styles.modalInputLabel}>2. Archivo de Video:</label>
              <div
                onClick={() => !isUploading && uploadFileInputRef.current?.click()}
                style={{
                  ...styles.fileDropZone,
                  borderColor: uploadFile ? '#16a34a' : '#cbd5e1',
                  backgroundColor: uploadFile ? '#f0fdf4' : '#fafafa'
                }}
              >
                {uploadFile ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <FaCheckCircle size={24} color="#16a34a" />
                    <div style={{ textAlign: 'left' }}>
                      <strong style={{ display: 'block', fontSize: 13, color: '#166534' }}>
                        {uploadFile.name}
                      </strong>
                      <span style={{ fontSize: 11, color: '#15803d' }}>
                        {(uploadFile.size / (1024 * 1024)).toFixed(2)} MB • Toca para cambiar archivo
                      </span>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                    <div style={styles.fileDropIconWrap}>
                      <FaCloudUploadAlt size={22} color={APP_PRIMARY} />
                    </div>
                    <div>
                      <span style={{ fontSize: 13, fontWeight: '700', color: APP_PRIMARY }}>
                        Seleccionar archivo de video
                      </span>
                      <span style={{ display: 'block', fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                        Formatos admitidos: MP4, MOV (hasta 200 MB)
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={styles.modalInputLabel}>3. Título visible del ejercicio:</label>
              <input
                type="text"
                value={uploadNombre}
                disabled={isUploading}
                onChange={(e) => setUploadNombre(e.target.value)}
                style={styles.modalTextInput}
                placeholder="Ej: 1 - Día 1 - Calentamiento"
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={styles.modalInputLabel}>4. Descripción o instrucciones:</label>
              <textarea
                value={uploadDescripcion}
                disabled={isUploading}
                onChange={(e) => setUploadDescripcion(e.target.value)}
                style={styles.modalTextArea}
                placeholder="• 4 series de 12 repeticiones&#10;• Descanso de 60 segundos"
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => setShowUploadModal(false)}
                disabled={isUploading}
                style={styles.btnModalCancel}
              >
                Cancelar
              </button>
              <button
                onClick={handleExecuteUpload}
                disabled={isUploading}
                style={styles.btnModalPrimary}
              >
                {isUploading ? 'Subiendo...' : 'Subir Ejercicio'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Insertar Video en Medio */}
      {showInsertModal && insertAfterVideo && (
        <div style={modalOverlay} onClick={() => !isInserting && setShowInsertModal(false)}>
          <div style={modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, color: '#1e293b', fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
                <FaPlusCircle color={APP_PRIMARY} /> Insertar video en la rutina
              </h3>
              {!isInserting && (
                <button
                  onClick={() => setShowInsertModal(false)}
                  style={styles.modalCloseIcon}
                >
                  ✕
                </button>
              )}
            </div>

            <p style={styles.modalDesc}>
              El nuevo ejercicio se insertará exactamente después de:{' '}
              <strong style={{ color: '#1e293b' }}>
                "{insertAfterVideo.nombreVisible || insertAfterVideo.nombreArchivo}"
              </strong>.
              <br />
              <span style={{ color: APP_PRIMARY, fontWeight: '600' }}>
                ✓ Los videos posteriores de ese día se renumerarán automáticamente sin alterar las rutinas ya asignadas.
              </span>
            </p>

            <input
              type="file"
              accept="video/*,image/jpeg,image/png,image/jpg"
              ref={insertFileInputRef}
              disabled={isInserting}
              style={{ display: 'none' }}
              onChange={(e) => setInsertFile(e.target.files[0] || null)}
            />

            <div style={{ marginBottom: 16 }}>
              <label style={styles.modalInputLabel}>1. Archivo de Video:</label>
              <div
                onClick={() => !isInserting && insertFileInputRef.current?.click()}
                style={{
                  ...styles.fileDropZone,
                  borderColor: insertFile ? '#16a34a' : '#cbd5e1',
                  backgroundColor: insertFile ? '#f0fdf4' : '#fafafa'
                }}
              >
                {insertFile ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <FaCheckCircle size={24} color="#16a34a" />
                    <div style={{ textAlign: 'left' }}>
                      <strong style={{ display: 'block', fontSize: 13, color: '#166534' }}>
                        {insertFile.name}
                      </strong>
                      <span style={{ fontSize: 11, color: '#15803d' }}>
                        {(insertFile.size / (1024 * 1024)).toFixed(2)} MB • Toca para cambiar archivo
                      </span>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                    <div style={styles.fileDropIconWrap}>
                      <FaCloudUploadAlt size={22} color={APP_PRIMARY} />
                    </div>
                    <div>
                      <span style={{ fontSize: 13, fontWeight: '700', color: APP_PRIMARY }}>
                        Seleccionar archivo de video
                      </span>
                      <span style={{ display: 'block', fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                        Formatos admitidos: MP4, MOV (hasta 200 MB)
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={styles.modalInputLabel}>2. Nombre visible correlativo:</label>
              <input
                type="text"
                value={insertNombre}
                disabled={isInserting}
                onChange={(e) => setInsertNombre(e.target.value)}
                style={styles.modalTextInput}
                placeholder="Ej: 2 - Día 1 - Sentadilla búlgara"
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={styles.modalInputLabel}>3. Descripción o instrucciones:</label>
              <textarea
                value={insertDescripcion}
                disabled={isInserting}
                onChange={(e) => setInsertDescripcion(e.target.value)}
                style={styles.modalTextArea}
                placeholder="• 4 series de 12 repeticiones&#10;• Descanso de 60 segundos"
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => setShowInsertModal(false)}
                disabled={isInserting}
                style={styles.btnModalCancel}
              >
                Cancelar
              </button>
              <button
                onClick={handleExecuteInsert}
                disabled={isInserting}
                style={styles.btnModalPrimary}
              >
                {isInserting ? 'Subiendo y renumerando...' : 'Insertar y Renumerar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Ver descripción completa */}
      {modalDescripcion && (
        <div style={modalOverlay} onClick={() => setModalDescripcion(null)}>
          <div style={modalBoxSmall} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, color: '#1e293b', fontSize: 17, fontWeight: '700' }}>
                {modalDescripcion.titulo}
              </h3>
              <button
                onClick={() => setModalDescripcion(null)}
                style={styles.modalCloseIcon}
              >
                ✕
              </button>
            </div>
            <div
              style={{
                color: '#475569',
                fontSize: 13,
                lineHeight: 1.6,
                margin: '14px 0 20px 0',
                whiteSpace: 'pre-line',
                maxHeight: '55vh',
                overflowY: 'auto'
              }}
            >
              {modalDescripcion.texto}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setModalDescripcion(null)} style={styles.btnModalPrimary}>
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Asignar Plan Completo */}
      {showBulkAssignModal && (
        <div style={modalOverlay} onClick={() => !isBulkAssigning && setShowBulkAssignModal(false)}>
          <div style={modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, color: '#15803d', display: 'flex', alignItems: 'center', gap: 8, fontSize: 18 }}>
                <FaBolt /> Asignar Plan Completo
              </h3>
              {!isBulkAssigning && (
                <button
                  onClick={() => setShowBulkAssignModal(false)}
                  style={styles.modalCloseIcon}
                >
                  ✕
                </button>
              )}
            </div>

            <p style={styles.modalDesc}>
              Esta acción asignará todos los <strong>{videos.length} videos</strong> de la categoría{' '}
              <em>"{prettyCategoryName(categoria)}"</em> al alumno seleccionado, organizándolos en sus días respectivos.
            </p>

            <div style={{ marginBottom: 16 }}>
              <label style={styles.modalInputLabel}>1. Selecciona el Usuario:</label>
              <Select
                options={usuarioOptions}
                placeholder="Buscar usuario..."
                isSearchable
                filterOption={filterOption}
                onChange={(o) => setBulkUid(o ? o.value : '')}
                value={usuarioOptions.find((u) => u.value === bulkUid) || null}
                styles={customSelectStyles}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={styles.modalInputLabel}>2. Nombre del Plan a asignar:</label>
              <Select
                options={planOptions}
                placeholder="Selecciona el plan..."
                onChange={(o) => setBulkPlan(o ? o.value : '')}
                value={planOptions.find((p) => p.value === bulkPlan) || null}
                styles={customSelectStyles}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => setShowBulkAssignModal(false)}
                disabled={isBulkAssigning}
                style={styles.btnModalCancel}
              >
                Cancelar
              </button>
              <button
                onClick={handleBulkAssign}
                disabled={isBulkAssigning}
                style={styles.btnBulkModalConfirm}
              >
                {isBulkAssigning ? 'Asignando videos...' : 'Confirmar y Asignar Todo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Quitar Plan Completo */}
      {showBulkRemoveModal && (
        <div style={modalOverlay} onClick={() => !isBulkRemoving && setShowBulkRemoveModal(false)}>
          <div style={modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 8, fontSize: 18 }}>
                <FaUserMinus /> Quitar Plan Completo
              </h3>
              {!isBulkRemoving && (
                <button
                  onClick={() => setShowBulkRemoveModal(false)}
                  style={styles.modalCloseIcon}
                >
                  ✕
                </button>
              )}
            </div>

            <p style={styles.modalDesc}>
              Esta acción removerá la asignación de <strong>todos los videos</strong> de este plan para el usuario en esta categoría.
            </p>

            <div style={{ marginBottom: 16 }}>
              <label style={styles.modalInputLabel}>1. Selecciona el Usuario:</label>
              <Select
                options={usuarioOptions}
                placeholder="Buscar usuario..."
                isSearchable
                filterOption={filterOption}
                onChange={(o) => setBulkRemoveUid(o ? o.value : '')}
                value={usuarioOptions.find((u) => u.value === bulkRemoveUid) || null}
                styles={customSelectStyles}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={styles.modalInputLabel}>2. Plan a desasignar:</label>
              <Select
                options={planOptions}
                placeholder="Selecciona el plan..."
                onChange={(o) => setBulkRemovePlan(o ? o.value : '')}
                value={planOptions.find((p) => p.value === bulkRemovePlan) || null}
                styles={customSelectStyles}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => setShowBulkRemoveModal(false)}
                disabled={isBulkRemoving}
                style={styles.btnModalCancel}
              >
                Cancelar
              </button>
              <button
                onClick={handleBulkRemove}
                disabled={isBulkRemoving}
                style={styles.btnBulkRemoveConfirm}
              >
                {isBulkRemoving ? 'Eliminando...' : 'Confirmar y Quitar Plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Eliminar Video */}
      {showDeleteModal && (
        <div style={modalOverlay} onClick={() => setShowDeleteModal(false)}>
          <div style={modalBoxSmall} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#dc2626', marginTop: 0, fontSize: 17 }}>Confirmar eliminación</h3>
            <p style={{ color: '#64748b', fontSize: 13, lineHeight: 1.5, margin: '0 0 20px 0' }}>
              ¿Estás seguro que deseas eliminar{' '}
              <strong>"{videoToDelete?.nombreVisible || videoToDelete?.nombreArchivo}"</strong>?
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={styles.btnModalCancel}
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                style={styles.btnModalDanger}
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Editar Video */}
      {showEditModal && (
        <div style={modalOverlay} onClick={() => setShowEditModal(false)}>
          <div style={modalBox} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, color: '#1e293b', fontSize: 18 }}>Editar detalles del video</h3>
            <div style={{ marginBottom: 14 }}>
              <label style={styles.modalInputLabel}>Nombre visible:</label>
              <input
                type="text"
                placeholder="Ej: 1 - Día 1 - Sentadilla búlgara con mancuerna"
                value={editNombre}
                onChange={(e) => setEditNombre(e.target.value)}
                style={styles.modalTextInput}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={styles.modalInputLabel}>Descripción o instrucciones:</label>
              <textarea
                placeholder="Describe series, repeticiones y postura..."
                value={editDescripcion}
                onChange={(e) => setEditDescripcion(e.target.value)}
                style={styles.modalTextArea}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => setShowEditModal(false)}
                style={styles.btnModalCancel}
              >
                Cancelar
              </button>
              <button
                onClick={saveEdits}
                style={styles.btnModalPrimary}
              >
                Guardar cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Recomendación */}
      {showRecModal && videoRecTarget && (
        <div style={modalOverlay} onClick={() => setShowRecModal(false)}>
          <div style={modalBox} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, color: '#1e293b', fontSize: 18 }}>Indicación personalizada</h3>
            <div style={{ marginBottom: 12, color: '#64748b', fontSize: 13 }}>
              Para:{' '}
              <strong style={{ color: '#1e293b' }}>
                {usuarios.find((u) => u.uid === recForUid)?.nombre || recForUid}
              </strong>
            </div>
            <textarea
              placeholder="Ej: Recuerda no bloquear las rodillas al subir y cuidar el peso..."
              value={recommendationText}
              onChange={(e) => setRecommendationText(e.target.value)}
              style={styles.modalTextArea}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
              <button
                onClick={() => {
                  setShowRecModal(false);
                  setRecommendationText('');
                  setVideoRecTarget(null);
                  setRecForUid('');
                }}
                style={styles.btnModalCancel}
              >
                Cancelar
              </button>
              <button onClick={saveRecommendation} style={styles.btnModalPrimary}>
                Guardar indicación
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dock inferior flotante */}
      <div style={navDockStyles.dockContainer}>
        <div style={navDockStyles.dockPill}>
          <button
            type="button"
            onClick={() => navigate('/admin')}
            style={navDockStyles.dockButton}
          >
            <div style={navDockStyles.iconWrapper}>
              <FaHome size={14} />
            </div>
            <span>Inicio</span>
          </button>

          <div style={navDockStyles.divider} />

          <button
            type="button"
            onClick={handleLogout}
            style={{ ...navDockStyles.dockButton, color: '#dc2626' }}
          >
            <div
              style={{
                ...navDockStyles.iconWrapper,
                backgroundColor: '#fee2e2',
                color: '#dc2626'
              }}
            >
              <FaSignOutAlt size={13} />
            </div>
            <span>Cerrar sesión</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================== ESTILOS =========================== */
const styles = {
  page: {
    background: '#f8fafc',
    minHeight: '100vh',
    padding: '24px 16px 120px 16px',
    boxSizing: 'border-box',
    fontFamily: "'Poppins', sans-serif"
  },
  container: {
    maxWidth: 1200,
    margin: '0 auto'
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16
  },
  backButton: {
    background: '#fdf2f8',
    color: APP_PRIMARY,
    border: '1px solid #fbcfe8',
    padding: '8px 16px',
    borderRadius: 50,
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: 13,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    transition: 'all 0.2s ease'
  },
  headerBadge: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: APP_PRIMARY,
    background: '#fdf2f8',
    padding: '4px 12px',
    borderRadius: 20
  },
  titleSection: {
    marginBottom: 20
  },
  title: {
    fontSize: 26,
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 4px 0',
    letterSpacing: '-0.3px'
  },
  subtitle: {
    fontSize: 13,
    color: '#64748b',
    margin: 0
  },
  actionToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 18
  },
  leftActions: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap'
  },
  rightActions: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap'
  },
  btnUpload: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 18px',
    background: APP_PRIMARY,
    color: '#ffffff',
    border: 'none',
    borderRadius: 50,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: '600',
    boxShadow: '0 3px 10px rgba(176, 48, 96, 0.25)',
    transition: 'all 0.2s ease'
  },
  btnReorganize: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 16px',
    background: '#ffffff',
    color: APP_PRIMARY,
    border: `1.5px solid ${APP_PRIMARY}`,
    borderRadius: 50,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: '600',
    transition: 'all 0.2s ease'
  },
  btnBulkAssign: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 16px',
    background: '#f0fdf4',
    color: '#15803d',
    border: '1.5px solid #bbf7d0',
    borderRadius: 50,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: '600',
    transition: 'all 0.2s ease'
  },
  btnBulkRemove: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 16px',
    background: '#fef2f2',
    color: '#dc2626',
    border: '1.5px solid #fecaca',
    borderRadius: 50,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: '600',
    transition: 'all 0.2s ease'
  },
  searchWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    marginBottom: 20
  },
  searchIcon: {
    position: 'absolute',
    left: 16,
    color: '#94a3b8',
    fontSize: 13
  },
  searchInput: {
    width: '100%',
    padding: '11px 18px 11px 42px',
    borderRadius: 50,
    border: '1.5px solid #e2e8f0',
    fontSize: 13,
    outline: 'none',
    color: '#1e293b',
    background: '#ffffff',
    boxSizing: 'border-box'
  },
  emptyCard: {
    background: '#ffffff',
    borderRadius: 20,
    padding: '40px 20px',
    textAlign: 'center',
    border: '1px dashed #cbd5e1'
  },
  videosList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14
  },
  videoCard: {
    background: '#ffffff',
    borderRadius: 20,
    border: '1px solid #f1f5f9',
    boxShadow: '0 4px 18px rgba(0, 0, 0, 0.03)',
    padding: '18px 20px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 20,
    flexWrap: 'wrap'
  },
  mediaContainer: {
    position: 'relative',
    width: 170,
    height: 100,
    borderRadius: 12,
    overflow: 'hidden',
    background: '#0f172a',
    flexShrink: 0
  },
  mediaElement: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  mediaBadge: {
    position: 'absolute',
    bottom: 6,
    right: 6,
    background: 'rgba(15, 23, 42, 0.75)',
    color: '#ffffff',
    fontSize: 10,
    fontWeight: '700',
    padding: '2px 6px',
    borderRadius: 4,
    textTransform: 'uppercase'
  },
  infoCol: {
    flex: '1 1 260px',
    minWidth: 240
  },
  videoTitleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 10,
    marginBottom: 6
  },
  videoTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
    lineHeight: 1.3
  },
  videoTopActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },
  iconReplaceBtn: {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: '1px solid #bbf7d0',
    background: '#f0fdf4',
    color: '#16a34a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  iconEditBtn: {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: '1px solid #e2e8f0',
    background: '#ffffff',
    color: '#64748b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  iconDeleteBtn: {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: '1px solid #fecaca',
    background: '#fef2f2',
    color: '#dc2626',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  btnInsertAfter: {
    background: '#fdf2f8',
    color: APP_PRIMARY,
    border: '1px dashed #fbcfe8',
    padding: '6px 12px',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    transition: 'all 0.2s ease'
  },
  videoDescription: {
    fontSize: 12,
    color: '#475569',
    lineHeight: 1.4,
    margin: '0 0 6px 0',
    cursor: 'pointer',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  },
  descHint: {
    fontSize: 11,
    color: '#94a3b8'
  },
  daySelectorGroup: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 8
  },
  daySelectBtn: {
    padding: '9px 12px',
    borderRadius: 8,
    border: '1.5px solid',
    fontSize: 13,
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none'
  },
  fileDropZone: {
    border: '2px dashed #cbd5e1',
    borderRadius: 14,
    padding: '20px 16px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  fileDropIconWrap: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    backgroundColor: '#fdf2f8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto'
  },
  assignCol: {
    flex: '1 1 360px',
    minWidth: 280,
    borderLeft: '1px solid #f1f5f9',
    paddingLeft: 20
  },
  assignTitleRow: {
    marginBottom: 8
  },
  assignSectionLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: '#475569',
    textTransform: 'uppercase',
    letterSpacing: '0.4px'
  },
  assignControlsGrid: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    marginBottom: 10
  },
  btnAssignSingle: {
    padding: '8px 14px',
    borderRadius: 8,
    border: 'none',
    background: APP_PRIMARY,
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
    cursor: 'pointer',
    height: 38,
    whiteSpace: 'nowrap',
    boxShadow: '0 2px 6px rgba(176, 48, 96, 0.2)'
  },
  assignedAccordionBox: {
    marginTop: 6
  },
  accordionTrigger: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '7px 12px',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: '600',
    outline: 'none',
    fontFamily: 'inherit',
    transition: 'all 0.2s ease'
  },
  accordionContent: {
    maxHeight: 180,
    overflowY: 'auto',
    marginTop: 6,
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },
  assignedUserItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#f8fafc',
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #f1f5f9'
  },
  assignedUserName: {
    fontSize: 12,
    fontWeight: '700',
    color: '#1e293b'
  },
  assignedUserPlan: {
    fontSize: 11,
    color: '#64748b'
  },
  btnRec: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    background: '#f1f5f9',
    color: '#334155',
    border: '1px solid #cbd5e1',
    padding: '4px 8px',
    borderRadius: 6,
    fontSize: 11,
    cursor: 'pointer',
    fontWeight: '600'
  },
  btnRemoveAssignment: {
    background: 'none',
    border: 'none',
    color: '#dc2626',
    cursor: 'pointer',
    padding: 2,
    display: 'flex',
    alignItems: 'center'
  },
  unassignedLabel: {
    color: '#94a3b8',
    fontSize: 12,
    fontStyle: 'italic',
    padding: '6px 0'
  },
  paginationRow: {
    marginTop: 24,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8
  },
  pageBtn: {
    padding: '6px 12px',
    borderRadius: 8,
    border: '1.5px solid #e2e8f0',
    background: '#ffffff',
    color: '#1e293b',
    fontSize: 12,
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14
  },
  modalCloseIcon: {
    background: 'none',
    border: 'none',
    fontSize: 16,
    cursor: 'pointer',
    color: '#94a3b8'
  },
  modalDesc: {
    color: '#64748b',
    fontSize: 13,
    margin: '0 0 16px 0',
    lineHeight: 1.5
  },
  modalInputLabel: {
    display: 'block',
    fontWeight: '600',
    fontSize: 12,
    color: '#475569',
    marginBottom: 6
  },
  modalTextInput: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid #cbd5e1',
    fontSize: 13,
    boxSizing: 'border-box',
    outline: 'none'
  },
  modalTextArea: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid #cbd5e1',
    fontSize: 13,
    minHeight: 100,
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'inherit'
  },
  btnModalCancel: {
    padding: '9px 16px',
    borderRadius: 50,
    border: '1px solid #e2e8f0',
    background: '#ffffff',
    color: '#64748b',
    fontSize: 13,
    fontWeight: '600',
    cursor: 'pointer'
  },
  btnModalPrimary: {
    padding: '9px 18px',
    borderRadius: 50,
    border: 'none',
    background: APP_PRIMARY,
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 3px 10px rgba(176, 48, 96, 0.25)'
  },
  btnBulkModalConfirm: {
    padding: '9px 18px',
    borderRadius: 50,
    border: 'none',
    background: '#15803d',
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 3px 10px rgba(21, 128, 61, 0.25)'
  },
  btnBulkRemoveConfirm: {
    padding: '9px 18px',
    borderRadius: 50,
    border: 'none',
    background: '#dc2626',
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 3px 10px rgba(220, 38, 38, 0.25)'
  },
  btnModalDanger: {
    padding: '9px 18px',
    borderRadius: 50,
    border: 'none',
    background: '#dc2626',
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
    cursor: 'pointer'
  }
};

const modalOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  background: 'rgba(15, 23, 42, 0.4)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 3000,
  padding: 16,
  boxSizing: 'border-box'
};

const modalBox = {
  background: '#ffffff',
  padding: 24,
  borderRadius: 20,
  width: '100%',
  maxWidth: 580,
  boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
  border: '1px solid #f1f5f9'
};

const modalBoxSmall = {
  background: '#ffffff',
  padding: 24,
  borderRadius: 18,
  width: '100%',
  maxWidth: 420,
  boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
  border: '1px solid #f1f5f9'
};

const navDockStyles = {
  dockContainer: {
    position: 'fixed',
    bottom: 20,
    left: 0,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    pointerEvents: 'none',
    padding: '0 16px',
    boxSizing: 'border-box'
  },
  dockPill: {
    pointerEvents: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.94)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: '1.5px solid rgba(241, 245, 249, 0.95)',
    borderRadius: 50,
    padding: '6px 12px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease'
  },
  dockButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: 'none',
    borderRadius: 50,
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '13px',
    fontFamily: "'Poppins', sans-serif",
    transition: 'all 0.2s ease',
    outline: 'none',
    backgroundColor: 'transparent',
    color: '#64748b',
    fontWeight: '600'
  },
  iconWrapper: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    backgroundColor: '#f1f5f9',
    color: '#64748b'
  },
  divider: {
    width: 1,
    height: 22,
    backgroundColor: '#e2e8f0',
    margin: '0 2px'
  }
};